<template>
  <component
    :is="as"
    :type="as === 'button' ? type : undefined"
    :class="['cg-btn', `cg-btn--${variant}`, `cg-btn--${size}`, block ? 'is-block' : '', disabled ? 'is-disabled' : '', className]"
    :disabled="as === 'button' ? disabled : undefined"
    :aria-disabled="as !== 'button' ? (disabled ? 'true' : undefined) : undefined"
    @click="onClick"
  >
    <span v-if="icon && iconPosition === 'left'" class="cg-btn__icon" :class="{ 'is-spin': iconSpin }" aria-hidden="true">
      <Icon :name="icon" :size="iconSize" />
    </span>
    <span v-if="$slots.default" class="cg-btn__label"><slot /></span>
    <span v-if="icon && iconPosition === 'right'" class="cg-btn__icon" aria-hidden="true">
      <Icon :name="icon" :size="iconSize" />
    </span>
  </component>
</template>

<script setup lang="ts">
import Icon from "@/components/ui/Icon.vue";

const props = withDefaults(
  defineProps<{
    as?: "button" | "a";
    type?: "button" | "submit" | "reset";
    variant?: "primary" | "outline" | "ghost" | "soft";
    size?: "sm" | "md" | "lg" | "icon";
    block?: boolean;
    disabled?: boolean;
    icon?: string;
    iconPosition?: "left" | "right";
    iconSize?: number;
    iconSpin?: boolean;
    className?: string;
  }>(),
  {
    as: "button",
    type: "button",
    variant: "primary",
    size: "md",
    block: false,
    disabled: false,
    icon: "",
    iconPosition: "left",
    iconSize: 18,
    iconSpin: false,
    className: "",
  }
);

const emit = defineEmits<{
  (e: "click", ev: MouseEvent): void;
}>();

function onClick(ev: MouseEvent) {
  if (props.disabled) {
    ev.preventDefault();
    ev.stopPropagation();
    return;
  }
  emit("click", ev);
}
</script>

<style scoped lang="scss">
.cg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid transparent;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  font-weight: 650;
  letter-spacing: -0.01em;
  transition:
    background 160ms ease,
    color 160ms ease,
    border-color 160ms ease,
    transform 160ms ease,
    opacity 160ms ease;
}

.cg-btn.is-block {
  width: 100%;
}

.cg-btn.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.cg-btn:active {
  transform: translateY(1px);
}

.cg-btn:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 2px color-mix(in oklch, var(--ring) 40%, transparent),
    0 0 0 4px color-mix(in oklch, var(--background) 80%, transparent);
}

.cg-btn__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cg-btn__icon.is-spin {
  animation: cg-spin 900ms linear infinite;
}

@keyframes cg-spin {
  to {
    transform: rotate(360deg);
  }
}

.cg-btn--sm {
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
}

.cg-btn--md {
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
}

.cg-btn--lg {
  height: 44px;
  padding: 0 18px;
  font-size: 15px;
}

.cg-btn--icon {
  height: 40px;
  width: 40px;
  padding: 0;
}

.cg-btn--primary {
  background: var(--foreground);
  color: var(--background);
}

.cg-btn--primary:hover {
  background: color-mix(in oklch, var(--foreground) 92%, transparent);
}

.cg-btn--outline {
  background: transparent;
  color: var(--foreground);
  border-color: color-mix(in oklch, var(--border) 85%, transparent);
}

.cg-btn--outline:hover {
  background: var(--muted);
}

.cg-btn--ghost {
  background: transparent;
  color: var(--foreground);
}

.cg-btn--ghost:hover {
  background: var(--muted);
}

.cg-btn--soft {
  background: color-mix(in oklch, var(--foreground) 7%, transparent);
  color: var(--foreground);
}

.cg-btn--soft:hover {
  background: color-mix(in oklch, var(--foreground) 10%, transparent);
}
</style>

